/**
 * Argus — LiveScreenPlayer: MSE wrapper pentru afișare live screen capture.
 *
 * Codec: H.264 Main 4.0 (`avc1.4D4028`) — supports 1920x1080 @ ≤30fps + Baseline content (subset).
 * 2026-05-10: switch de la `avc1.42c028` (Baseline) la `avc1.4D4028` (Main) — encoder emite
 * Main profile pentru quality=hd/hd_plus (profile_idc=77 în h264_mf args), iar declarele Baseline
 * la addSourceBuffer cauza throw "codec not supported" pe Chromium strict (mai ales single-codec
 * mime fără audio). Main accept + decode Baseline content + Main content uniform.
 * Backpressure: queue >50 chunks → drop oldest 25 + UI badge "Lag detectat".
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import { getOperatorWsUrl } from '../lib/streams';

interface Props {
  streamId: string;
  onTerminated: (reason: string) => void;
  hasAudio?: boolean;
  muted?: boolean;
  volume?: number; // 0..1 — bloc webrtc-audio-support 2026-05-11
  flipHorizontal?: boolean; // mirror video CSS — used in webcam context (selfie-style)
}

/**
 * Bloc recording-browser-pivot 2026-05-09: handle expose pentru parent care vrea
 * MediaStream din `<video>` element (`captureStream()` API) ca să-l hrănească într-un
 * `MediaRecorder` browser-side. ZERO impact pe stream playback — captureStream() doar
 * tap-uie pe MediaStream-ul deja decodat de MSE.
 */
export interface LivePlayerHandle {
  getMediaElement: () => HTMLVideoElement | HTMLAudioElement | null;
  captureStream: () => MediaStream | null;
}

const HEADER_SIZE = 24;
// Main 4.0 H.264 (profile_idc=77, level 4.0). AAC-LC if audio multiplexed.
// Main superset of Baseline → decoder accepts both encoder profiles (66/77 produced by ResolveQuality).
const CODEC_VIDEO_ONLY = 'video/mp4; codecs="avc1.4D4028"';
const CODEC_VIDEO_AUDIO = 'video/mp4; codecs="avc1.4D4028,mp4a.40.2"';
const MAX_QUEUE = 500;

function LiveScreenPlayerImpl({ streamId, onTerminated, hasAudio = false, muted = false, volume = 1, flipHorizontal = false }: Props, ref: React.ForwardedRef<LivePlayerHandle>): JSX.Element {
  const CODEC = hasAudio ? CODEC_VIDEO_AUDIO : CODEC_VIDEO_ONLY;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (containerRef.current) await containerRef.current.requestFullscreen();
    } catch (err) { console.warn('Fullscreen toggle failed', err); }
  };

  const togglePictureInPicture = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else await v.requestPictureInPicture();
    } catch (err) { console.warn('PiP toggle failed', err); }
  };
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [lag, setLag] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    getMediaElement: () => videoRef.current,
    captureStream: () => {
      const v = videoRef.current as (HTMLVideoElement & { captureStream?: () => MediaStream }) | null;
      if (!v) return null;
      try { return v.captureStream?.() ?? null; }
      catch (err) { console.warn('captureStream() failed', err); return null; }
    },
  }), []);

  // Stable ref so onTerminated changes don't trigger useEffect re-run (which would
  // recreate MediaSource mid-stream and lose init segment context).
  const onTerminatedRef = useRef(onTerminated);
  useEffect(() => { onTerminatedRef.current = onTerminated; }, [onTerminated]);

  // Bloc webrtc-audio-support 2026-05-11: propagă volume la `<video>.volume` (0..1).
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.volume = Math.max(0, Math.min(1, volume));
    }
  }, [volume]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const ms = new MediaSource();
    video.src = URL.createObjectURL(ms);
    let sb: SourceBuffer | null = null;
    const queue: ArrayBuffer[] = [];
    let ws: WebSocket | null = null;
    let cancelled = false;

    let appendCounter = 0;
    const flushQueue = () => {
      if (sb && !sb.updating && queue.length > 0) {
        const chunk = queue.shift();
        if (chunk) {
          try {
            sb.appendBuffer(chunk);
            appendCounter++;
            if (appendCounter <= 5 || appendCounter % 50 === 0) {
              console.log(`MSE append #${appendCounter} size=${chunk.byteLength} queueLen=${queue.length}`);
            }
          } catch (err) {
            console.warn(`SourceBuffer.appendBuffer #${appendCounter + 1} size=${chunk.byteLength} FAILED`, err);
          }
        }
      }
    };

    console.log(`LiveScreenPlayer: useEffect mount streamId=${streamId}`);
    ms.addEventListener('sourceended', () => console.log('MSE sourceended'));
    ms.addEventListener('sourceclose', () => console.log('MSE sourceclose'));
    video.addEventListener('error', () => {
      const err = video.error;
      console.error('VIDEO ERROR', err?.code, err?.message);
    });
    ms.addEventListener('sourceopen', () => {
      console.log('MSE sourceopen — adding SourceBuffer');
      try {
        sb = ms.addSourceBuffer(CODEC);
        sb.mode = 'sequence';
        sb.addEventListener('updateend', () => {
          flushQueue();
          // Live catch-up smooth via playbackRate: ușor accelerăm dacă rămâne în urmă față de live-edge.
          // Seek hard doar dacă lag > 5s (situație extremă, salvare după blocaj).
          if (sb && sb.buffered.length > 0) {
            const end = sb.buffered.end(sb.buffered.length - 1);
            const lag = end - video.currentTime;
            if (lag > 5.0) {
              video.currentTime = Math.max(0, end - 0.3);
              video.playbackRate = 1.0;
            } else if (lag > 1.0) {
              video.playbackRate = 1.1; // catch up gradual
            } else if (lag < 0.3) {
              video.playbackRate = 1.0;
            }
          }
        });
        sb.addEventListener('error', (e) => {
          console.error(`SourceBuffer error after ${appendCounter} appends`, e, 'mediaError=', video.error);
        });
        console.log('MSE SourceBuffer added OK with codec', CODEC);
      } catch (err) {
        console.error('MSE addSourceBuffer FAILED', err, 'codec=', CODEC);
        setError(`H.264 Main codec (${CODEC}) not supported in this browser.`);
        return;
      }

      const wsUrl = getOperatorWsUrl();
      console.log(`MSE opening WebSocket to ${wsUrl}`);
      ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';

      ws.addEventListener('open', () => {
        console.log(`WS open — sending subscribe_stream streamId=${streamId}`);
        ws?.send(JSON.stringify({ type: 'subscribe_stream', streamId }));
      });

      let initSeen = false;
      ws.addEventListener('message', (e) => {
        if (typeof e.data === 'string') {
          try {
            const json = JSON.parse(e.data);
            if (json.type === 'stream_terminated') {
              onTerminatedRef.current(json.reason ?? 'unknown');
              return;
            }
            if (json.type === 'stream_lag') {
              setLag(true);
            }
          } catch {}
          return;
        }
        if (cancelled) return;
        const ab = e.data as ArrayBuffer;
        if (ab.byteLength < HEADER_SIZE) return;

        // IPC binary frame header (24 bytes): [16] streamId UUID + [1] chunk_type + [1] flags + [4] sequence + [2] reserved
        const view = new DataView(ab);
        const chunkType = view.getUint8(16);
        const payload = ab.slice(HEADER_SIZE);

        if (chunkType === 0) {
          // Init segment (ftyp+moov). First arrival sets up MSE codec.
          // Subsequent arrivals (e.g. agent ffmpeg restart with new SPS/PPS) require resetting SourceBuffer
          // because each SourceBuffer accepts ONE init in 'sequence' mode; a second init would be parsed as media → garbage decode.
          if (initSeen && sb && !sb.updating) {
            console.log('MSE: new init segment received — resetting SourceBuffer');
            try {
              sb.abort();
              sb.remove(0, Number.POSITIVE_INFINITY);
            } catch (err) { console.warn('MSE: abort/remove on re-init failed', err); }
          }
          initSeen = true;
        }

        queue.push(payload);
        if (queue.length > MAX_QUEUE) {
          // Don't drop — H.264 inter-frames depend on previous frames; gap = decoder error.
          setLag(true);
          if (queue.length % 100 === 0) console.warn(`Stream lag — queue=${queue.length}`);
        }
        flushQueue();
      });

      ws.addEventListener('close', () => {
        if (!cancelled) onTerminatedRef.current('ws_closed');
      });

      ws.addEventListener('error', () => {
        if (!cancelled) setError('Stream connection error.');
      });
    });

    video.play().catch(() => {});

    return () => {
      cancelled = true;
      try {
        ws?.send(JSON.stringify({ type: 'unsubscribe_stream', streamId }));
        ws?.close();
      } catch {}
      try {
        if (ms.readyState === 'open') ms.endOfStream();
      } catch {}
      try {
        URL.revokeObjectURL(video.src);
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  return (
    <div
      ref={containerRef}
      className="live-screen-player"
      style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' }}
      onDoubleClick={() => void toggleFullscreen()}
      onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}
    >
      {error !== null && <div className="stream-error">{error}</div>}
      {lag && <div className="stream-lag-badge">Lag detected</div>}
      <video
        ref={videoRef}
        autoPlay
        muted={muted}
        playsInline
        crossOrigin="use-credentials"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', transform: flipHorizontal ? 'scaleX(-1)' : undefined }}
      />
      <Menu
        open={ctxMenu !== null}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
      >
        <MenuItem onClick={() => { void togglePictureInPicture(); setCtxMenu(null); }}>
          <ListItemIcon><PictureInPictureAltIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Picture in Picture</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { void toggleFullscreen(); setCtxMenu(null); }}>
          <ListItemIcon>{isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}</ListItemIcon>
          <ListItemText>{isFullscreen ? 'Exit Fullscreen' : 'Full Screen'}</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}

const LiveScreenPlayer = forwardRef<LivePlayerHandle, Props>(LiveScreenPlayerImpl);
export default LiveScreenPlayer;
